import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Dayjs } from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import ActivityBarChart from "../../ActivityBarChart";
import MetricTable from "../MetricTable";
import ExportMenu from "../ExportMenu";
import { buildMetricGrid } from "../print/metricGrid";
import { fmt } from "../useReportFilter";
import {
  CORE_METRIC_FALLBACK_LABELS,
  CORE_METRIC_IDS,
  metricNumber,
  useMetrics,
} from "../useMetrics";
import { ConsultantOption } from "../types";
import { DrilldownTarget } from "../DrilldownDrawer";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

const Card = ({ children, sx }: { children: any; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

export interface ConsultantDetailProps {
  consultantId: string;
  consultantName: string;
  /** Kept for callers; the report is now driven by the metrics endpoint only. */
  consultantUser?: ConsultantOption;
  start: Dayjs | null;
  end: Dayjs | null;
  onBack: () => void;
  onDrill: (target: DrilldownTarget) => void;
}

/** One consultant: core-metric chart, monthly trend, full metric list, export. */
const ConsultantDetail: React.FC<ConsultantDetailProps> = ({
  consultantId,
  consultantName,
  start,
  end,
  onBack,
  onDrill,
}) => {
  const [trendMetric, setTrendMetric] = useState<string>(CORE_METRIC_IDS[0]);

  const totals = useMetrics({
    start,
    end,
    consultant: consultantId,
    groupBy: "none",
  });
  const monthly = useMetrics({
    start,
    end,
    consultant: consultantId,
    groupBy: "month",
  });

  const catalogById = useMemo(
    () => new Map((totals.data?.catalog || []).map((e) => [e.id, e])),
    [totals.data],
  );

  const labelFor = (id: string) =>
    catalogById.get(id)?.label || CORE_METRIC_FALLBACK_LABELS[id] || id;

  // ActivityBarChart consumes [{ key, value }] pairs.
  const coreChartData = useMemo(
    () =>
      CORE_METRIC_IDS.map((id) => ({
        key: labelFor(id),
        value: metricNumber(totals.data?.total, id),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [totals.data],
  );

  const trendData = useMemo(
    () =>
      (monthly.data?.groups || []).map((group) => ({
        key: group.label || group.key,
        value: metricNumber(group.metrics, trendMetric),
      })),
    [monthly.data, trendMetric],
  );

  const trendOptions = useMemo(() => {
    const catalog = monthly.data?.catalog || totals.data?.catalog || [];
    if (catalog.length > 0) {
      return catalog
        .filter(
          (e) => e.id !== "referralTypes" && e.id !== "assistanceRequested",
        )
        .map((e) => ({ id: e.id, label: e.label }));
    }
    return CORE_METRIC_IDS.map((id) => ({ id, label: labelFor(id) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthly.data, totals.data]);

  const slug = consultantName.replace(/\s+/g, "-");
  const rangeSlug = `${fmt(start) || "all"}_${fmt(end) || "today"}`;

  // Print report is a standalone route so it can be a real browser print /
  // "Save as PDF" instead of a client-side @react-pdf render.
  const openPrintReport = () => {
    const query = new URLSearchParams();
    if (consultantId) query.set("consultant", consultantId);
    if (start) query.set("start", fmt(start));
    if (end) query.set("end", fmt(end));
    if (consultantName) query.set("name", consultantName);
    window.open(`/reportprint/consultant?${query.toString()}`, "_blank");
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 2, gap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ color: PRIMARY }}
          >
            All consultants
          </Button>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c2c34" }}>
            {consultantName}
          </Typography>
        </Stack>
        <ExportMenu
          getGrid={() => buildMetricGrid(totals.data)}
          filename={`metrics_${slug}_${rangeSlug}`}
          onPrint={openPrintReport}
          disabled={!totals.data}
        />
      </Stack>

      {totals.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {totals.error}
        </Alert>
      )}

      <Stack spacing={3}>
        <Card>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "#2c2c34",
              borderLeft: `4px solid ${PRIMARY}`,
              pl: 1.5,
              mb: 2,
            }}
          >
            Core metrics
          </Typography>
          {totals.loading && !totals.data ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={24} sx={{ color: PRIMARY }} />
            </Stack>
          ) : (
            <Box sx={{ height: 320 }}>
              <ActivityBarChart data={coreChartData} />
            </Box>
          )}
        </Card>

        <Card>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            sx={{ mb: 2, gap: 1 }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "#2c2c34",
                borderLeft: `4px solid ${PRIMARY}`,
                pl: 1.5,
              }}
            >
              Monthly trend
            </Typography>
            <Select
              size="small"
              value={trendMetric}
              onChange={(e) => setTrendMetric(String(e.target.value))}
              sx={{ minWidth: 220 }}
            >
              {trendOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          {monthly.error ? (
            <Alert severity="warning">{monthly.error}</Alert>
          ) : monthly.loading && !monthly.data ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={24} sx={{ color: PRIMARY }} />
            </Stack>
          ) : trendData.length === 0 ? (
            <Typography sx={{ color: "#8a8a93" }}>
              No monthly data in this range.
            </Typography>
          ) : (
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 20, bottom: 30, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="key" tick={{ fontSize: 11 }} interval={0} />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Bar
                    dataKey="value"
                    fill={PRIMARY}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  >
                    <LabelList dataKey="value" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Card>

        <Card>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "#2c2c34",
              borderLeft: `4px solid ${PRIMARY}`,
              pl: 1.5,
              mb: 2,
            }}
          >
            All metrics
          </Typography>
          <MetricTable
            data={totals.data}
            loading={totals.loading}
            error={totals.error}
            exportName={`metrics_${slug}`}
            onCellClick={(row) =>
              onDrill({
                id: row.id,
                label: row.label,
                consultant: consultantId,
                consultantLabel: consultantName,
              })
            }
          />
        </Card>
      </Stack>
    </Box>
  );
};

export default ConsultantDetail;
